import * as React from 'react'
import { useState, useCallback } from 'react'
import styled from 'styled-typed'
import { Redirect, Route, Switch, useParams } from 'react-router-dom'
import { History, Location } from 'history'
import { Account } from 'auth/state'
import {
    Container,
    ContainerBody,
    ContainerHeading,
    FullWidthContainerBody,
    HeadingSectionName,
} from 'uiComponents/settingsContainer'
import { getBaseKnowledgeUrl } from './utils'
import { PageTitle } from 'uiComponents/typography'
import { withFeatures } from 'features'
import { StatsService } from 'http/statsService'
import Permission from 'admin/permissionRequired'
import { TicketTemplatesService } from 'ticketTemplates/ticketTemplatesService'
import { parseSearch } from 'navigation'
import { ArticleService } from 'admin/articleService'
import { AddNewTopButton, ExceptionsTopButtons, ProductsTopButtons } from 'products/topButtons'
import { ProductListWithPath } from 'products/crud/common'
import { ManagementService } from 'settings/accountSettings/contract/managementService'
import ProductPricingDetailRouter from 'products/pricing/productPricingDetailRouter'
import PricingExceptionPage from 'products/pricing/exceptions/exceptionDetailPage'
import PricingExceptionsListPage from 'products/pricing/exceptions/exceptionsListPage'
import { PricingService } from 'products/pricing/pricingService'
import ValidityExceptionsListPage from 'products/validity/exceptions/exceptionsList'
import ValidityExceptionPage from 'products/validity/exceptions/exceptionDetailPage'
import ProductValidityDetailPage from 'products/validity/articleDetailPage'
import { ArticleConfigurationService } from 'products/articleConfigurationService'
import TimeSlotsListPage from 'timeSlots/list'
import TimeSlotGroupForm from 'timeSlots/form'
import InventoryListPage from 'inventory/list'
import OptionGroupsListPage from 'products/options/groupsList'
import OptionGroupDetails from 'products/options/groupDetails'
import OptionItemDetails from 'products/options/optionItemDetails'
import { OptionsService } from 'products/options/optionsService'
import CrowdControlForm from 'inventory/crowdControl'
import InventoryRuleForm from 'inventory/ruleEditForm'
import { ChannelsService } from 'channels/channelsService'
import HomePage from 'products/home'
import ProductTable from './productTable'
import BaseKnowledgeLink from 'uiComponents/typography/BaseKnowledgeLink'
import { useMessages } from 'messagesContext'
import { usePermission } from 'admin/hocs'
import WithRoutePermission from 'routes/guards/withRoutePermission'
import { BaseRouteParams } from 'hocs'
import { PricingPlansService } from './pricing/pricingPlans'

const Header = styled.div`
    margin-bottom: 1.375em;
    position: relative;
    opacity: 1;
`

interface ProductsContainerProps {
    history: History
    accounts: Account[]
    location: Location
    articleConfigurationService: ArticleConfigurationService
    articleService: ArticleService
    pricingService: PricingService
    pricingPlansService: PricingPlansService
    channelsService: ChannelsService
    managementService: ManagementService
    ticketTemplatesService: TicketTemplatesService
    statsService: StatsService
    optionsService: OptionsService
    hasFeature: (feature: string, accountSlug: string) => boolean
}

const ProductsContainer: React.FC<ProductsContainerProps> = (props) => {
    const { location, history, pricingService, articleConfigurationService, statsService, managementService } = props
    const { hasPermission } = usePermission()
    const { accountSlug } = useParams<BaseRouteParams>()
    const [header, setHeader] = useState('Products Overview')
    const [subheader, setSubheader] = useState<string>('')
    const [activeSection, setActiveSection] = useState('productsList')
    const [flattenedCategories] = useState<ProductListWithPath[] | null>(null)
    const [loadingProducts, setLoadingProducts] = useState(false)
    const [showBulkButtons, setShowBulkButtons] = useState(false)
    const [bulkEditPricing, setBulkEditPricing] = useState(false)
    const [bulkEditValidity, setBulkEditValidity] = useState(false)
    const { removeAllMessages, hideMessage, replaceMessages, addMessage } = useMessages()

    const setActiveSectionAndHeader = useCallback(
        (section: string, newHeader?: string) => {
            const newSubheader = getBaseKnowledgeUrl(section)
            setSubheader(newSubheader)

            if (newHeader) {
                setHeader(newHeader)
            }

            if (
                section === 'productsList' &&
                (location.pathname.includes('crud') || location.pathname.includes('detail'))
            ) {
                return
            }

            setActiveSection(section)
        },
        [location.pathname],
    )

    const generateParentsPath = useCallback(
        async (
            array: { name: string; path: string; id: string }[],
            id: string,
            allLists: ProductListWithPath[] = flattenedCategories || [],
        ): Promise<{ name: string; path: string; id: string }[] | null> => {
            try {
                const parentPl = allLists.find((l) => l.uuid === id)
                const parentCategoryPath = {
                    name: parentPl?.name || 'Unknown',
                    id: id,
                    path: `/account/${accountSlug}/products/home?listIds=`,
                }
                array.unshift(parentCategoryPath)

                const parentId = parentPl?.parentIds[parentPl?.parentIds.length - 2]
                if (parentId) {
                    return await generateParentsPath(array, parentId, allLists)
                } else {
                    return array
                }
            } catch {
                replaceMessages('server_error', 'error', 'Oops! An unknown error occurred.')
                return null
            }
        },
        [flattenedCategories, accountSlug, props],
    )

    const params = parseSearch(location.search)
    const viewMode =
        location.pathname.indexOf('nested') > -1
            ? 'nested'
            : location.pathname.indexOf('flat') > -1
            ? 'flat'
            : ((params.from || 'nested') as 'nested' | 'flat')

    type SourceType = '?from=flat' | '?from=nested'
    const source = `?from=${params.from || viewMode}` as SourceType

    const routePermissionCheckData = {
        accounts: props.accounts,
        accountSlug: accountSlug,
        history: props.history,
        hasPermission: hasPermission,
    }

    const navigationTabs: {
        key: string
        id?: string
        active: boolean
        to: string
        label: string
        shouldShow: boolean
    }[] = [
        {
            key: 'productsList',
            active: activeSection === 'productsList',
            to: `/account/${accountSlug}/products/home/${viewMode}`,
            label: 'Products',
            shouldShow: true,
        },
        {
            key: 'pricingExceptions',
            id: 'exceptionsTab',
            active: activeSection === 'pricingExceptions',
            to: `/account/${accountSlug}/products/pricing/exceptions/list${source}`,
            label: 'Pricing exceptions',
            shouldShow: props.hasFeature('Pricing', accountSlug) && hasPermission('view_pricing_settings', accountSlug),
        },
        {
            key: 'validityExceptions',
            id: 'exceptionsTab',
            active: activeSection === 'validityExceptions',
            to: `/account/${accountSlug}/products/validity/exceptions/list${source}`,
            label: 'Validity exceptions',
            shouldShow: hasPermission('view_product_validity', accountSlug),
        },
        {
            key: 'timeSlots',
            id: 'timeSlotsTab',
            active: activeSection === 'timeSlots',
            to: `/account/${accountSlug}/products/time_slots/home${source}`,
            label: 'Time slots',
            shouldShow: hasPermission('partner_admin', accountSlug),
        },
        {
            key: 'optionGroups',
            id: 'optionGroupsTab',
            active: activeSection === 'optionGroups',
            to: `/account/${accountSlug}/products/option_groups/home${source}`,
            label: 'Option groups',
            shouldShow: hasPermission('partner_admin', accountSlug),
        },
        {
            key: 'inventory',
            id: 'inventoryTab',
            active: activeSection === 'inventory',
            to: `/account/${accountSlug}/products/inventory/home${source}`,
            label: 'Crowd Control',
            shouldShow: hasPermission('partner_admin', accountSlug),
        },
    ].filter((tab) => tab.shouldShow)

    return (
        <div style={{ position: 'relative' }} id="pricing-settings">
            <Header
                style={{
                    marginBottom: '0',
                    minHeight: '0',
                }}
            >
                <PageTitle data-userpilot="products-page-header">{header}</PageTitle>
            </Header>
            {(location.pathname.includes('products/home') || location.pathname.includes('/products/productTable')) && (
                <ProductsTopButtons
                    showBulkButtons={showBulkButtons}
                    accountSlug={accountSlug}
                    loading={loadingProducts}
                    showAdjustView={!location.pathname.includes('/products/productTable')}
                    showBulkEditPricing={() => setBulkEditPricing(true)}
                    showBulkEditValidity={() => setBulkEditValidity(true)}
                    origin={viewMode}
                    history={props.history}
                />
            )}
            <Permission name="edit_pricing_settings" accountSlug={accountSlug}>
                {location.pathname.indexOf('pricing/exceptions/list') > -1 && (
                    <ExceptionsTopButtons accountSlug={accountSlug} for="pricing" />
                )}
            </Permission>
            <Permission name="edit_product_validity" accountSlug={accountSlug}>
                {location.pathname.indexOf('validity/exceptions/list') > -1 && (
                    <ExceptionsTopButtons accountSlug={accountSlug} for="validity" />
                )}
            </Permission>
            <Permission name="partner_admin" accountSlug={accountSlug}>
                {location.pathname.indexOf('option_groups/home') > -1 && (
                    <AddNewTopButton accountSlug={accountSlug} for="option_groups" />
                )}
                {location.pathname.indexOf('inventory/home') > -1 && (
                    <AddNewTopButton accountSlug={accountSlug} for="inventory" />
                )}
                {location.pathname.indexOf('time_slots/home') > -1 && (
                    <AddNewTopButton accountSlug={accountSlug} for="time_slots" />
                )}
            </Permission>
            <Container style={{ marginBottom: '0' }}>
                <ContainerHeading style={{ paddingLeft: '0.3em' }}>
                    {navigationTabs.map((tab) => (
                        <HeadingSectionName
                            key={tab.key}
                            id={tab.id}
                            className={tab.active ? 'active' : ''}
                            to={tab.to}
                        >
                            {tab.label}
                        </HeadingSectionName>
                    ))}
                </ContainerHeading>
                <Switch location={location}>
                    <Route path="/account/:accountSlug/products/productTable" component={ProductTable} />
                    <Route
                        path="/account/:accountSlug/products/pricing/:id/detail"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <ContainerBody>
                                    <ProductPricingDetailRouter
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        pricingPlansService={props.pricingPlansService}
                                        channelsService={props.channelsService}
                                        articleConfigurationService={articleConfigurationService}
                                        history={history}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        hideTopMessage={hideMessage}
                                        hasPermission={hasPermission}
                                        viewMode={viewMode}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/pricing/exceptions/list"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <FullWidthContainerBody>
                                    <PricingExceptionsListPage
                                        accountSlug={accountSlug}
                                        history={history}
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        managementService={managementService}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        hasPermission={hasPermission}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/pricing/exceptions/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_pricing_settings']}>
                                <ContainerBody>
                                    <PricingExceptionPage
                                        pricingService={pricingService}
                                        articleService={props.articleService}
                                        channelsService={props.channelsService}
                                        statsService={statsService}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceTopMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        hasPermission={hasPermission}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/:id/detail"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <ContainerBody>
                                    <ProductValidityDetailPage
                                        history={history}
                                        location={location}
                                        setActiveSection={setActiveSectionAndHeader}
                                        hasFeature={props.hasFeature}
                                        viewMode={viewMode}
                                    />
                                </ContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/exceptions/list"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <FullWidthContainerBody>
                                    <ValidityExceptionsListPage
                                        articleConfigurationService={articleConfigurationService}
                                        articleService={props.articleService}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        standalonePage
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/validity/exceptions/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['view_product_validity']}>
                                <ValidityExceptionPage
                                    articleConfigurationService={articleConfigurationService}
                                    articleService={props.articleService}
                                    history={history}
                                    location={location}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceTopMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <TimeSlotsListPage
                                        history={props.history}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        hasPermission={hasPermission}
                                        accountSlug={accountSlug}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/edit/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <TimeSlotGroupForm
                                    history={props.history}
                                    accountSlug={accountSlug}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/time_slots/duplicate/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <TimeSlotGroupForm
                                    prototype
                                    history={props.history}
                                    accountSlug={accountSlug}
                                    setActiveSection={setActiveSectionAndHeader}
                                    replaceMessages={replaceMessages}
                                    removeAllMessages={removeAllMessages}
                                />
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionGroupsListPage
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        optionsService={props.optionsService}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/group_details/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionGroupDetails
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        optionsService={props.optionsService}
                                        articleService={props.articleService}
                                        setActiveSection={setActiveSectionAndHeader}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/option_groups/option_item/:groupUuid/:itemId"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <OptionItemDetails
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                        optionsService={props.optionsService}
                                        articleService={props.articleService}
                                        setActiveSection={setActiveSectionAndHeader}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/inventory/home"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <InventoryListPage
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        source={source}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/inventory/item/:id"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <CrowdControlForm
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path="/account/:accountSlug/products/inventory/rule/:ruleId/item/:inventoryId"
                        render={() => (
                            <WithRoutePermission requiredPermissions={['partner_admin']}>
                                <FullWidthContainerBody>
                                    <InventoryRuleForm
                                        history={props.history}
                                        accountSlug={accountSlug}
                                        setActiveSection={setActiveSectionAndHeader}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </FullWidthContainerBody>
                            </WithRoutePermission>
                        )}
                    />
                    <Route
                        path={['/account/:accountSlug/products/home', '/account/:accountSlug/products/crud']}
                        render={() => (
                            <HomePage
                                location={location}
                                accountSlug={accountSlug}
                                pricingService={pricingService}
                                articleService={props.articleService}
                                optionsService={props.optionsService}
                                history={history}
                                setActiveSection={setActiveSectionAndHeader}
                                replaceTopMessages={replaceMessages}
                                hasPermission={hasPermission}
                                hasFeature={props.hasFeature}
                                addMessage={addMessage}
                                removeAllMessages={removeAllMessages}
                                hideMessage={hideMessage}
                                routePermissionCheckData={routePermissionCheckData}
                                ticketTemplatesService={props.ticketTemplatesService}
                                viewMode={viewMode}
                                setShowBulkButtons={setShowBulkButtons}
                                bulkEditPricing={bulkEditPricing}
                                bulkEditValidity={bulkEditValidity}
                                cancelBulkEditPricing={() => setBulkEditPricing(false)}
                                cancelBulkEditValidity={() => setBulkEditValidity(false)}
                                loading={loadingProducts}
                                setLoading={setLoadingProducts}
                            />
                        )}
                    />
                    <Route render={() => <Redirect to={`/account/${accountSlug}/products/home/nested`} />} />
                </Switch>
            </Container>
            <BaseKnowledgeLink link={subheader} />
        </div>
    )
}

export default withFeatures(ProductsContainer)
